import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, NavLink, Link, Outlet } from 'react-router-dom';
import { Button } from '@mantine/core';
import { PERSONS_PATH } from '@/lib/navigation';
import { setWebTitle } from '@/lib/format-utils';
import { useBusiness, useUpdateBusiness, useCreatePerson } from '@/hooks/api-hooks';
import PageHeader from '@/components/page-header';
import BusinessPageHeader from '@/components/business-page-header';
import CardModal from '@/components/card-modal';
import CreateOrSelectPerson from '@/components/create-or-select-person-component';
import { SkeletonListWithFilters } from '@/components/snippets';
import { NotFound } from '@/components/not-found';
import type { Business, Person } from '@turizi/shared';
import { localTenant } from '@/services/session-service';
import {
    IconWallet,
    IconInfoCircle,
    IconUsers,
    IconSettings,
    IconAirBalloonFilled,
    IconCashBanknote,
    IconBuildingStore,
    IconTrash,
    IconUser,
    IconUserPlus,
    IconLink,
    IconLinkOff,
    IconArrowsExchange,
    IconArrowUpRight,
    IconCalendarCheck,
    IconMail,
} from '@tabler/icons-react';

const DEFAULT_TAB = 'details';


export default function EditBusinessPage(): JSX.Element {
    const { t } = useTranslation();

    const { id } = useParams<{ id: string }>();
    const { pathname } = useLocation();

    // Hooks to load data
    const {
        data,
        isLoading
    } = useBusiness(id);
    const business = data?.data;

    const tenant = useMemo(() => localTenant(), []);
    const isOwner = tenant && business && business.owner_id === tenant.id;
    const isManager = tenant && business && business.manager_id === tenant.id;
    const canEditBusiness = Boolean(isOwner || isManager);

    const updateBusinessMutation = useUpdateBusiness();
    const createPersonMutation = useCreatePerson();
    const [isPaxModalOpen, setIsPaxModalOpen] = useState(false);

    const activeSection = pathname.split('/').pop() || DEFAULT_TAB;

    setWebTitle(`${business?.category ? t(business.category) + ' ' : ''}${business?.name || ''} · ${t(activeSection)}`);

    const linkedPerson = business?.person || (business?.persons && business.persons.length > 0 ? business.persons[0] : null);
    const linkedPersonId = business?.person_id || linkedPerson?.id;
    const linkedPersonName = linkedPerson?.name;

    const handleLinkPax = (selectedPax: Person) => {
        if (!business?.id || !selectedPax?.id) return;
        updateBusinessMutation.mutate(
            { id: business.id, person_id: selectedPax.id },
            {
                onSuccess: () => {
                    setIsPaxModalOpen(false);
                },
            }
        );
    };

    const handleCreateAsPax = () => {
        if (!business?.id) return;
        const primaryPhone = business.phones ? Object.values(business.phones).find(Boolean) : undefined;
        const primaryEmail = business.emails ? Object.values(business.emails).find(Boolean) : undefined;
        const staffCategories = ['guide', 'coordinator', 'driver'];
        const paxCategory = staffCategories.includes(business.category) ? 'staff' : 'pax';

        createPersonMutation.mutate(
            {
                name: business.name,
                category: paxCategory,
                phone: primaryPhone || null,
                email: primaryEmail || null,
                document_type: business.tax_id_type || null,
                document_number: business.tax_id_number || null,
                tax_condition: business.tax_condition || '5',
                country_code: business.country_code || null,
                address: business.address || null,
                business_id: business.id,
            },
            {
                onSuccess: (res) => {
                    const createdPax = res?.data;
                    if (createdPax?.id && business.id) {
                        updateBusinessMutation.mutate({ id: business.id, person_id: createdPax.id });
                    }
                },
                onError: () => {
                    setIsPaxModalOpen(true);
                },
            }
        );
    };

    const handleUnlinkPax = () => {
        if (!business?.id) return;
        if (!window.confirm(t('confirm_unlink_pax'))) return;
        updateBusinessMutation.mutate({ id: business.id, person_id: null });
    };

    const sectionItems = useMemo(() => {
        const items = [
            { key: 'details', label: t('details'), icon: IconInfoCircle },
            { key: 'balance', label: t('balance'), icon: IconWallet },
            { key: 'payments', label: t('payments'), icon: IconCashBanknote },
            { key: 'reservations_as_client', label: t('reservations_as_client'), icon: IconCalendarCheck },
            { key: 'bookings_as_provider', label: t('bookings_as_provider'), icon: IconBuildingStore },
            { key: 'users', label: t('users'), icon: IconUsers },
            { key: 'emails', label: t('emails'), icon: IconMail },
            { key: 'commercial', label: t('commercial_settings'), icon: IconSettings },
            { key: 'provider_services', label: t('provider_services'), icon: IconAirBalloonFilled },
            { key: 'deleted_reservations', label: t('deleted_reservations'), icon: IconTrash },
        ];
        if (isOwner) {
            return items.filter(item => item.key !== 'commercial');
        }
        return items;
    }, [t, isOwner]);

    if (isLoading && !business) {
        return (
            <div>
                <PageHeader
                    title={t('business')}
                />
                <div className="m-4">
                    <SkeletonListWithFilters />
                </div>
            </div>
        );
    }

    if (!isLoading && (!business?.id)) {
        return <NotFound type="business" />;
    }

    return (
        <div className="space-y-4">
            {/* Top Header Card */}
            {business && (
                <BusinessPageHeader
                    business={business}
                    isLoading={false}
                />
            )}

            {/* Sidebar + Main Content Column */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                {/* Left Side Menu (Sidebar) */}
                <aside className="w-full md:w-[220px] shrink-0">
                    <div className="sticky top-20 space-y-3">
                        <div className="flex flex-row md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 border-b border-gray-200 md:border-b-0">
                            {sectionItems.map((section) => {
                                const Icon = section.icon;
                                return (
                                    <NavLink
                                        key={section.key}
                                        to={section.key}
                                        className={({ isActive }) =>
                                            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150 ${isActive
                                                ? 'bg-gray-100 text-gray-900 shadow-xs'
                                                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50/80'
                                            }`
                                        }
                                        end
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <Icon size={16} className={
                                                    isActive
                                                        ? 'text-gray-900 shrink-0'
                                                        : 'text-gray-400 shrink-0'} stroke={2}
                                                />
                                                <span className={isActive
                                                    ? 'text-gray-900 whitespace-nowrap'
                                                    : 'text-gray-500 whitespace-nowrap'
                                                }>{section.label}</span>
                                            </>
                                        )}
                                    </NavLink>
                                );
                            })}
                        </div>

                        {/* Person (Pax) Relationship Card */}
                        <div className="md:pt-2 md:border-t md:border-gray-200">
                            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 space-y-2.5">
                                <div className="flex items-center gap-1.5">
                                    <IconUser size={15} className="text-blue-600 shrink-0" stroke={2} />
                                    <span className="text-xs font-bold text-slate-800 leading-tight">
                                        {t('pax_profile_title')}
                                    </span>
                                </div>
                                <p className="text-[11px] leading-snug text-slate-500">
                                    {t('business_as_pax_explanation')}
                                </p>

                                {linkedPersonId ? (
                                    <div className="space-y-2">
                                        <Link
                                            target="_blank"
                                            to={`${PERSONS_PATH}/${linkedPersonId}`}
                                            title={t('view_pax_profile')}
                                            className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100/80 transition-all border border-blue-200/80 group"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="line-clamp-2 break-words leading-tight">
                                                    {linkedPersonName || t('linked_pax')}
                                                </div>
                                                <div className="text-[10px] font-normal text-blue-500 mt-0.5">
                                                    #{linkedPersonId}
                                                </div>
                                            </div>
                                            <IconArrowUpRight size={14} className="text-blue-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform shrink-0" />
                                        </Link>

                                        {canEditBusiness && (
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    size="compact-xs"
                                                    variant="subtle"
                                                    color="dark"
                                                    leftSection={<IconArrowsExchange size={12} />}
                                                    onClick={() => setIsPaxModalOpen(true)}
                                                    loading={updateBusinessMutation.isPending}
                                                    className="flex-1"
                                                >
                                                    {t('change_linked_pax')}
                                                </Button>
                                                <Button
                                                    size="compact-xs"
                                                    variant="subtle"
                                                    color="red"
                                                    leftSection={<IconLinkOff size={12} />}
                                                    onClick={handleUnlinkPax}
                                                    loading={updateBusinessMutation.isPending}
                                                >
                                                    {t('unlink')}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    canEditBusiness && (
                                        <div className="flex flex-col gap-1.5">
                                            <Button
                                                size="compact-xs"
                                                variant="subtle"
                                                color="black"
                                                fullWidth
                                                leftSection={<IconLink size={13} />}
                                                onClick={() => setIsPaxModalOpen(true)}
                                            >
                                                {t('link_pax')}
                                            </Button>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </aside>

                <CardModal
                    size="80%"
                    withCloseButton={true}
                    opened={isPaxModalOpen}
                    onClose={() => setIsPaxModalOpen(false)}
                    header={<div>{t('pax_profile_title')}</div>}
                >
                    <CreateOrSelectPerson
                        preloadName={business?.name || ''}
                        onSelect={handleLinkPax}
                    />
                </CardModal>

                {/* Right Side Content Column */}
                <main className="flex-1 min-w-0">
                    <Outlet context={business as Business} />
                </main>
            </div>
        </div>
    );
}